/**
 * @file Hub social : lieux fixes (lacs, maisonnettes, fontaine), arbres geants, decor de saison (date reelle), herbe a chat.
 */
import type { SpawnEntry } from './types';

export interface HubPlace {
  targetId: string;
  x: number;
  y: number;
}

/** Rayon de la carte du hub (monde). */
export const HUB_RADIUS = 64;

/** Reglages de la camera du hub : zoom multiplicatif de la largeur cadree (1 = defaut). */
export const HUB_ZOOM = { min: 0.5, max: 2.2, step: 1.25 } as const;

/** Delai minimal entre deux feux d'artifice (secondes). */
export const FIREWORK_COOLDOWN = 2.5;

export const HUB_PLACES: readonly HubPlace[] = [
  // place centrale
  { targetId: 'decor_fountain', x: 0, y: 6 },
  // grand lac a l'ouest, etangs
  { targetId: 'decor_lake', x: -34, y: 10 },
  { targetId: 'decor_lake', x: 30, y: -34 },
  { targetId: 'decor_pond', x: 28, y: 22 },
  { targetId: 'decor_pond', x: -16, y: -40 },
  // maisonnettes autour de la place
  { targetId: 'decor_house', x: -14, y: 26 },
  { targetId: 'decor_house', x: 12, y: 30 },
  { targetId: 'decor_house', x: 36, y: 6 },
  { targetId: 'decor_house', x: -40, y: -20 },
  { targetId: 'decor_house', x: -2, y: -26 },
  { targetId: 'decor_house', x: 14, y: -14 },
  { targetId: 'decor_house', x: -26, y: 40 },
];

/** Emplacements des arbres geants (on peut passer dessous). Le def est tire cycliquement dans `HUB_SEASONS[saison].giants`. */
export const HUB_GIANT_SPOTS: ReadonlyArray<{ x: number; y: number }> = [
  { x: -24, y: -2 },
  { x: 22, y: -2 },
  { x: 0, y: -14 },
  { x: -8, y: 14 },
  { x: 24, y: 12 },
  { x: -30, y: -34 },
  { x: 44, y: -18 },
  { x: -48, y: 18 },
  { x: 6, y: 46 },
  { x: 50, y: 28 },
  { x: -14, y: -52 },
  { x: 20, y: -48 },
];

// ---------------------------------------------------------------- Saisons (date reelle, hemisphere nord)
export type HubSeason = 'spring' | 'summer' | 'autumn' | 'winter';
export const HUB_SEASON_IDS: readonly HubSeason[] = ['spring', 'summer', 'autumn', 'winter'];

/** Saison d'un instant (ms epoch) : printemps 20/03, ete 21/06, automne 22/09, hiver 21/12 (heure locale). */
export const seasonOf = (ms: number): HubSeason => {
  const d = new Date(ms);
  const key = (d.getMonth() + 1) * 100 + d.getDate();
  if (key >= 320 && key < 621) return 'spring';
  if (key >= 621 && key < 922) return 'summer';
  if (key >= 922 && key < 1221) return 'autumn';
  return 'winter';
};

export interface HubSeasonDef {
  id: HubSeason;
  /** Sol, ciel et brouillard du hub pour cette saison (meme forme que BiomeDef). */
  ground: { base: string; patch: string; accent: string };
  sky: string;
  fog: { color: string; near: number; far: number };
  /** Decor disperse (arbres, buissons, rochers, petits elements de saison). */
  scatter: SpawnEntry[];
  /** Arbres geants (ids de cibles) utilises cycliquement sur `HUB_GIANT_SPOTS`. */
  giants: string[];
}

const hd = (season: HubSeason, key: string, weight: number): SpawnEntry => ({ targetId: `hd_${season}_${key}`, weight });

export const HUB_SEASONS: Record<HubSeason, HubSeasonDef> = {
  spring: {
    id: 'spring',
    ground: { base: '#b9e8a2', patch: '#a3dc8b', accent: '#ffe3f0' },
    sky: '#d6f0ff',
    fog: { color: '#e4f6ee', near: 50, far: 110 },
    scatter: [hd('spring', 'oak', 3), hd('spring', 'blossom', 3), hd('spring', 'blossomWhite', 2), hd('spring', 'bush', 3), hd('spring', 'rock', 1), hd('spring', 'flowers', 4)],
    giants: ['hd_spring_giantOak', 'hd_spring_giantBlossom', 'hd_spring_giantWillow', 'hd_spring_giantPine'],
  },
  summer: {
    id: 'summer',
    ground: { base: '#9edc7e', patch: '#86cc6c', accent: '#ffe27a' },
    sky: '#c8ecff',
    fog: { color: '#dff5e6', near: 50, far: 110 },
    scatter: [hd('summer', 'oak', 4), hd('summer', 'palm', 2), hd('summer', 'bush', 3), hd('summer', 'rock', 1), hd('summer', 'flowers', 4)],
    giants: ['hd_summer_giantOak', 'hd_summer_giantPalm', 'hd_summer_giantWillow', 'hd_summer_giantBaobab', 'hd_summer_giantPine'],
  },
  autumn: {
    id: 'autumn',
    ground: { base: '#e3c98c', patch: '#d3b073', accent: '#c9873f' },
    sky: '#f4e3cf',
    fog: { color: '#f2e2cc', near: 50, far: 110 },
    scatter: [hd('autumn', 'maple', 3), hd('autumn', 'oakRed', 3), hd('autumn', 'larch', 2), hd('autumn', 'bush', 3), hd('autumn', 'rock', 1), hd('autumn', 'pumpkin', 2), hd('autumn', 'leaves', 4)],
    giants: ['hd_autumn_giantOak', 'hd_autumn_giantRed', 'hd_autumn_giantWillow', 'hd_autumn_giantPine'],
  },
  winter: {
    id: 'winter',
    ground: { base: '#eef6fb', patch: '#dcebf5', accent: '#ffffff' },
    sky: '#dcecf8',
    fog: { color: '#e8f2f9', near: 45, far: 100 },
    scatter: [hd('winter', 'fir', 4), hd('winter', 'oakFrost', 3), hd('winter', 'bush', 3), hd('winter', 'rock', 1), hd('winter', 'snowman', 2)],
    giants: ['hd_winter_giantPine', 'hd_winter_giantOak', 'hd_winter_giantWillow', 'hd_winter_giantPineBlue'],
  },
};

// ---------------------------------------------------------------- Herbe a chat (jetons)
/**
 * Brindilles d'herbe a chat : apparaissent au hasard sur la carte, la premiere equipe qui marche dessus la recolte (+1 jeton).
 * Local pour l'instant (RNG de spawn du core) ; plus tard le serveur du hub decidera des apparitions (voir net/protocol.ts `CatnipSpawn`).
 */
export const HUB_CATNIP = {
  targetId: 'decor_catnip',
  /** Brindilles presentes des l'entree dans le hub. */
  initial: 2,
  /** Nombre maximal simultane. */
  max: 6,
  /** Delai (s) entre deux apparitions, tire au hasard. */
  intervalMin: 7,
  intervalMax: 16,
  /** Distance (chat -> brindille) pour la recolter. */
  collectRadius: 1.15,
} as const;
